import * as React from "react";
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

import { Build, Figure, startFigure } from "../../figures/engine";

type Props = Omit<React.HTMLAttributes<HTMLDivElement>, "children"> & {
  /** Lays the figure out; a new one (new data) re-knots it. */
  build: Build;
  /** The pointer brushes the cords aside (default true). */
  push?: boolean;
  /** A mouse can grab a cord (default true). */
  grab?: boolean;
  /** How far past the wrapper the cords may reach, px. */
  bleed?: number;
  /** Knots with labels show them when pointed at. */
  tips?: boolean;
  /** Follows a label's link (a stripe clicked, or a link in its label). */
  onOpen?: (href: string) => void;
  /** Hung on the cords with `data-at`; in the flow without canvas. */
  children?: React.ReactNode;
};

/**
 * A figure of cords (src/figures/engine.ts) laid over whatever it wraps.
 * Where there's no canvas, it's just the wrapper and what's in it.
 */
const CordFigure: React.FC<Props> = ({
  build,
  push,
  grab,
  bleed,
  tips = false,
  onOpen,
  className,
  children,
  ...rest
}) => {
  const host = useRef<HTMLDivElement>(null);
  const tip = useRef<HTMLDivElement>(null);
  const fig = useRef<Figure | null>(null);
  // A figure keeps the options it started with, and follows links with
  // the latest `onOpen`.
  const start = useRef({ build, push, grab, bleed, links: !!onOpen });
  const open = useRef(onOpen);
  open.current = onOpen;

  useEffect(() => {
    const { build: first, links, ...options } = start.current;
    fig.current = startFigure(host.current!, first, {
      ...options,
      tip: tip.current ?? undefined,
      open: links ? (href) => open.current!(href) : undefined,
    });
    return () => fig.current?.stop();
  }, []);

  useEffect(() => {
    if (build !== start.current.build) fig.current?.relayout(build);
  }, [build]);

  return (
    <>
      <div
        ref={host}
        className={className ? `fig ${className}` : "fig"}
        {...rest}
      >
        {children}
      </div>
      {tips &&
        createPortal(
          <div ref={tip} className="fig-tip" aria-hidden="true" />,
          document.body,
        )}
    </>
  );
};

export default CordFigure;
